import styled from '@emotion/styled';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { segment } from '@actions/analytics';
import Button from 'aquilla/core/molecules/Button';
import IconButton from 'aquilla/core/molecules/IconButton';
import { BATCH_SHARED, BATCH_KEYS } from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import Share from '@icon/Share';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import Dialog from '@comp/Dialog/Dialog';
import { Spacings } from '@styles/index';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import { getIsStorePath } from './utils';

const StyledShareIcon = styled(Share)`
  margin-bottom: -${Spacings.SPACING_1B};
`;

const StyledButton = styled(Button)`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const BatchShare = ({
  batchUID,
  batch,
  isPrimaryBatchEnabled = false,
  isIconButton = false,
  hasStorePurchase
}) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const closeShareOptiopns = () => setShowShareOptions(false);
  const { batchDetails } = useBatchDetails(batchUID);
  const batchGoalUID = batchDetails?.goal?.uid;
  const isSellable = batchDetails?.isSellable;
  const isMobile = useIsMobile();

  const goalSegmentData = useGoalEventProps({ goalUID: batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batchDetails,
    isBatch: true
  });
  const { route } = useRouter();
  const isStoreRoute = getIsStorePath(route);

  const handleShare = () => {
    segment.track(
      BATCH_SHARED,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batchDetails.state,
        last_primary_source: isStoreRoute ? 'Store' : 'Goal Home',
        is_sellable: isSellable,
        is_purchased: hasStorePurchase,
        is_store_exclusive: batchDetails?.isStoreExclusive
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    setShowShareOptions(true);
  };

  return (
    <>
      {isIconButton && !isMobile ? (
        <IconButton
          variant="secondary"
          color="white"
          size="medium"
          onClick={handleShare}
          $marginTop={isPrimaryBatchEnabled ? 0 : Spacings.SPACING_3B}
          isIconButton
        >
          <Share size={Spacings.SPACING_6B} color="var(--color-text-primary)" />
        </IconButton>
      ) : (
        <StyledButton
          variant="secondary"
          startIcon={
            <StyledShareIcon
              size={Spacings.SPACING_5B}
              color="var(--color-text-primary)"
            />
          }
          onClick={handleShare}
          size="large"
          $fullWidth
        >
          Share
        </StyledButton>
      )}
      <Dialog onClose={closeShareOptiopns} dialogState={showShareOptions}>
        <ShareDialog data={batchUID ? batchDetails : batch} />
      </Dialog>
    </>
  );
};

export default BatchShare;

BatchShare.propTypes = {
  batchUID: PropTypes.string.isRequired
};
